
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid } from '@mui/material'
import { Edit } from '@mui/icons-material'

import {
  useDELETE_gradeRiskCriteriaMutation,
  useGET_gradeRiskCriteriaByIdQuery,
  usePOST_gradeRiskCriteriaMutation,
  usePUT_gradeRiskCriteriaMutation,
} from 'redux/api/gradeRiskCriteriasApi'

import { useGET_subjectsSelectQuery } from 'redux/api/subjectsApi'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import InputField from 'components/atoms/fields/InputField'

import formModel, { initialValues, validationSchema } from 'utils/formModels/gradeRiskCriteriaModel'
import { crudColorMap, crudIconMap } from 'utils/helpers'

import { ActionType, IGradeRiskCriteria } from 'types'
import useCrudData from 'utils/customHooks/useCrudData'
import SelectField from 'components/atoms/fields/SelectField'

interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
}

const GradeRiskCriteriaForm: FC<IProps> = ({ action, id, viewOnly }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const backToSlug ='/grade-risk-criteria'
  const editSlug = `/grade-risk-criteria/edit/${id}`

  const {
    isLoading,
    entity,
    handleSubmit,
  } = useCrudData({
    backToSlug,
    entityName: `${t('school.grading-risk-criteria')}`,
    entityId: id,
    getEntityQuery: useGET_gradeRiskCriteriaByIdQuery,
    createEntityMutation: usePOST_gradeRiskCriteriaMutation,
    editEntityMutation: usePUT_gradeRiskCriteriaMutation,
    deleteEntityMutation: useDELETE_gradeRiskCriteriaMutation
  })

  const {
    subject,
    grade_threshold,
  } = formModel()
  
  const serverValues = entity?.id
    ? {
        ...entity
      }
    : null

  return (
    <Formik
      enableReinitialize
      initialValues={serverValues || initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const data: IGradeRiskCriteria = {
          subject: {
            id: entity?.id,
            title: entity?.title
          },
          subject_id: values.subject.id,
          grade_threshold: values.grade_threshold,
        }
        handleSubmit(action, id, data)
      }}
    >
      <Form>
        <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>
          <Grid item xs={12} md={6}>
            <SelectField
              fieldProps={{ ...subject }}
              apiCall={useGET_subjectsSelectQuery}
              disabled={viewOnly}
            />
          </Grid>
          <Grid item xs={12} md={6}>
             <InputField fieldProps={{ ...grade_threshold }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex' }} className="transparent-background">
              <Button
                variant='outlined'
                color='black'
                onClick={() => navigate(backToSlug)}
                sx={{ minHeight: '2.5rem', width: '10rem' }}
              >
                <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                {`${t('common.back')}`}
              </Button>
              {action === 'view' && 
                <Box sx={{ marginLeft: 'auto' }}>
                  <Button
                    variant='outlined'
                    color='black'
                    onClick={() => navigate(editSlug)}
                    sx={{ minHeight: '2.5rem', width: '10rem' }}
                  >
                    <Edit style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                    {`${t('common.edit')}`}
                  </Button>
                </Box>
              }
              {action !== 'view' && (
                <SubmitButton
                  color={crudColorMap[action]}
                  text={`${t('form-actions.' + action)}`}
                  icon={crudIconMap[action]}
                  sx={{ width: '10rem' }}
                  isLoading={isLoading}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Form>
    </Formik>
  )
}
export default GradeRiskCriteriaForm

